/**
 * Task 37, B6 — turning the engine's vocabulary into something a reseller can act on.
 *
 * THE REQUIREMENT IS "self-diagnose **and** resolve". Showing a reason is diagnosis only, so every
 * failure here also carries WHERE it is fixed. A reseller reading "no product is mapped" still has to
 * be told that the Products tab is where that happens, or they have been informed rather than helped.
 *
 * Import-free on purpose, so it runs under bare node in the verification harnesses without a bundler
 * — the same convention as Helpers/BillingSyncValidation.js and Task9Verification/Harnesses.
 */

/** The engine's nine job states, in words rather than in its own. */
const JOB_STATES = {
    pending: { label: "Waiting", tone: "secondary" },
    ready: { label: "Queued", tone: "secondary" },
    claimed: { label: "Starting", tone: "info" },
    running: { label: "Sending", tone: "info" },
    succeeded: { label: "Sent", tone: "success" },
    retryable_failed: { label: "Will try again", tone: "warning" },
    failed_manual: { label: "Needs attention", tone: "danger" },
    dead_lettered: { label: "Gave up", tone: "danger" },
    cancelled: { label: "Cancelled", tone: "secondary" },
};

/** The item statuses, for a line no job has been created for yet. */
const ITEM_STATES = {
    // N1 — NEW no longer means "about to go": nothing is sent until the reseller approves it.
    NEW: { label: "Awaiting approval", tone: "secondary" },
    APPROVED: { label: "Approved", tone: "secondary" },
    QUEUED: { label: "Queued", tone: "secondary" },
    SYNCED: { label: "Sent", tone: "success" },
    FAILED_MANUAL: { label: "Needs attention", tone: "danger" },
    DEAD_LETTERED: { label: "Gave up", tone: "danger" },
    DISCARDED: { label: "Discarded", tone: "secondary" },
    ACKNOWLEDGED: { label: "Acknowledged", tone: "secondary" },
};

/**
 * Item statuses that record a PERSON'S DECISION, which outranks whatever the job happens to say.
 *
 * Both discarding and acknowledging cancel the line's jobs, so asking the job first — as
 * describeState otherwise does, and rightly — would label these "Cancelled". That is not what
 * happened, and for an acknowledged failure it is close to the opposite: "cancelled" reads as the
 * charge having been stopped, when acknowledging explicitly does not claim that.
 */
const DECISIVE_ITEM_STATES = ["DISCARDED", "ACKNOWLEDGED"];

/**
 * What to call this line's state.
 *
 * The job is asked first where one exists, because it is the more specific of the two: an item reads
 * QUEUED for as long as its job is being sent, and "Sending" is the more useful word.
 */
export function describeState(line) {
    if (!line) return { label: "Unknown", tone: "secondary" };

    // A decision a person made is asked before the job, because the job was cancelled BY that
    // decision and would otherwise speak over it.
    if (line.itemStatus && DECISIVE_ITEM_STATES.includes(line.itemStatus)) {
        return ITEM_STATES[line.itemStatus];
    }

    const fromJob = line.jobStatus ? JOB_STATES[line.jobStatus] : null;
    if (fromJob) return fromJob;

    const fromItem = line.itemStatus ? ITEM_STATES[line.itemStatus] : null;
    if (fromItem) return fromItem;

    // An unrecognised value is shown as itself rather than hidden. A state nobody has a word for is
    // exactly the thing somebody needs to see in order to come and ask about it.
    return { label: line.jobStatus || line.itemStatus || "Unknown", tone: "secondary" };
}

/**
 * Where a failure is put right.
 *
 * Matched on the engine's and ConnectWise's own wording, which is why this is a list of signals
 * rather than a lookup: the text comes from a planner that writes for people and from a partner's API
 * that does not. Anything unmatched falls through to no destination, which the screen renders as the
 * reason alone — honest about not knowing rather than guessing at a tab.
 */
const DESTINATIONS = [
    {
        signals: ["no connectwise product is mapped", "product is mapped for this customer"],
        tab: "products",
        action: "Map a product for this package on the Products tab.",
    },
    {
        signals: ["no agreement", "not mapped to an agreement", "agreement for this customer"],
        tab: "customers",
        action: "Attach this customer to an agreement on the Customers tab.",
    },
    {
        signals: ["rejected those credentials", "private key", "could not be read", "unauthorized", "forbidden"],
        tab: "connection",
        action: "Check your ConnectWise credentials on the Connection tab.",
    },
    {
        signals: ["site address", "not found", "could not be reached", "did not respond"],
        tab: "connection",
        action: "Check your ConnectWise site address on the Connection tab.",
    },
    {
        signals: ["agreement does not exist", "agreement was not found", "cancelled agreement"],
        tab: "customers",
        action: "That agreement is no longer in ConnectWise. Choose another on the Customers tab.",
    },
];

/**
 * The tab that resolves this failure, and what to do there — or null when the text matches nothing
 * we recognise.
 */
export function describeResolution(lastError) {
    if (!lastError || typeof lastError !== "string") return null;

    const text = lastError.toLowerCase();

    for (const entry of DESTINATIONS) {
        if (entry.signals.some((signal) => text.includes(signal))) {
            return { tab: entry.tab, action: entry.action };
        }
    }

    return null;
}

/**
 * Whether the engine intends to try this one again on its own.
 *
 * Worth saying out loud: a reseller looking at a failure should know whether to wait or to act, and
 * "will try again at 14:05" answers that where "failed" does not.
 */
export function describeNextAttempt(line) {
    if (!line || !line.nextAttemptAt) return null;

    const due = new Date(line.nextAttemptAt);
    if (isNaN(due.getTime())) return null;

    return due > new Date()
        ? `Will try again at ${due.toLocaleTimeString()}`
        : "Due to try again shortly";
}

/** LICENCE and STORAGE, as the reseller's own billing calls them. */
export function describeAdditionType(additionType) {
    if (additionType === "STORAGE") return "Storage";
    if (additionType === "LICENCE") return "Licence";
    return additionType || "";
}
